import { Project } from '@applicator/shared'
import React from 'react'

import CustomCheckbox from '../CustomCheckbox'
import { ProjectBullet } from '../../utils/projectEditor'
import { WX_ADD_BULLET_BUTTON_CLASS, WX_BULLETS_BLOCK_CLASS } from './workExperienceLayout'
import WorkExperienceDragHandle from './WorkExperienceDragHandle'
import { getSaveHint } from './utils'

export interface ProjectEditorItemProps {
  project: Project
  projectIndex: number
  editingNameId: string | null
  editingCompanyId: string | null
  editingStartDateId: string | null
  editingEndDateId: string | null
  editingName: string
  editingCompany: string
  editingStartDate: string
  editingEndDate: string
  editingBulletId: string | null
  editingBulletText: string
  hoveredBulletId: string | null
  draggedBulletId: string | null
  draggedProjectId: string | null
  dragOverBulletIndex: number | null
  dragOverProjectIndex: number | null
  bulletTextareaRef: React.RefObject<HTMLTextAreaElement>
  setEditingNameId: (id: string | null) => void
  setEditingCompanyId: (id: string | null) => void
  setEditingStartDateId: (id: string | null) => void
  setEditingEndDateId: (id: string | null) => void
  setEditingName: (value: string) => void
  setEditingCompany: (value: string) => void
  setEditingStartDate: (value: string) => void
  setEditingEndDate: (value: string) => void
  setEditingBulletId: (id: string | null) => void
  setEditingBulletText: (value: string) => void
  setHoveredBulletId: (id: string | null) => void
  setDraggedBulletId: (id: string | null) => void
  setDraggedProjectId: (id: string | null) => void
  setDragOverBulletIndex: (index: number | null) => void
  setDragOverProjectIndex: (index: number | null) => void
  onUpdateProjectField: (
    projectId: string,
    field: 'name' | 'company' | 'startDate' | 'endDate',
    value: string
  ) => void
  onToggleProject: (projectId: string) => void
  onDeleteProject: (projectId: string) => void
  onAddBullet: (projectId: string) => void
  onToggleBullet: (projectId: string, bulletId: string) => void
  onDeleteBullet: (projectId: string, bulletId: string) => void
  onReorderBullets: (projectId: string, draggedBulletId: string, targetIndex: number) => void
  onReorderProjects: (draggedProjectId: string, targetIndex: number) => void
  onSaveBullet: (projectId: string, bulletId: string) => void
  onBulletTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  adjustTextareaHeight: (textarea: HTMLTextAreaElement) => void
}

function renderCheckbox(checked: boolean, onToggle: () => void) {
  return <CustomCheckbox checked={checked} onChange={onToggle} size="xs" />
}

export default function ProjectEditorItem({
  project,
  projectIndex,
  editingNameId,
  editingCompanyId,
  editingStartDateId,
  editingEndDateId,
  editingName,
  editingCompany,
  editingStartDate,
  editingEndDate,
  editingBulletId,
  editingBulletText,
  hoveredBulletId,
  draggedBulletId,
  draggedProjectId,
  dragOverBulletIndex,
  dragOverProjectIndex,
  bulletTextareaRef,
  setEditingNameId,
  setEditingCompanyId,
  setEditingStartDateId,
  setEditingEndDateId,
  setEditingName,
  setEditingCompany,
  setEditingStartDate,
  setEditingEndDate,
  setEditingBulletId,
  setEditingBulletText,
  setHoveredBulletId,
  setDraggedBulletId,
  setDraggedProjectId,
  setDragOverBulletIndex,
  setDragOverProjectIndex,
  onUpdateProjectField,
  onToggleProject,
  onDeleteProject,
  onAddBullet,
  onToggleBullet,
  onDeleteBullet,
  onReorderBullets,
  onReorderProjects,
  onSaveBullet,
  onBulletTextChange,
  adjustTextareaHeight,
}: ProjectEditorItemProps) {
  const bullets = project.bulletPoints ?? []
  const isProjectDragging = draggedProjectId === project.id
  const isProjectDragOver = dragOverProjectIndex === projectIndex && draggedProjectId !== project.id

  return (
    <div
      data-project-id={project.id}
      draggable
      onDragStart={(e) => {
        setDraggedProjectId(project.id)
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', '')
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (draggedProjectId && draggedProjectId !== project.id) {
          setDragOverProjectIndex(projectIndex)
        }
      }}
      onDragLeave={() => {
        if (dragOverProjectIndex === projectIndex) {
          setDragOverProjectIndex(null)
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        if (draggedProjectId && draggedProjectId !== project.id) {
          onReorderProjects(draggedProjectId, projectIndex)
        }
        setDraggedProjectId(null)
        setDragOverProjectIndex(null)
      }}
      onDragEnd={() => {
        setDraggedProjectId(null)
        setDragOverProjectIndex(null)
      }}
      className={`group/project flex items-start gap-2 cursor-move transition-all ${
        isProjectDragging ? 'opacity-50' : ''
      } ${isProjectDragOver ? 'ring-2 ring-interactive-focus-ring ring-inset rounded-lg' : ''}`}
    >
      <WorkExperienceDragHandle group="project" title="Перетащите проект для изменения порядка" />
      <div className="flex-1 min-w-0 space-y-2 border border-border-default rounded-lg p-3 bg-surface-default relative">
        <button
          type="button"
          onClick={() => onDeleteProject(project.id)}
          className="absolute top-2 right-2 p-1 text-text-muted hover:text-danger opacity-0 group-hover/project:opacity-100 transition-opacity"
          title="Delete project"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>

        <div className="flex items-start gap-3 pr-6">
          {renderCheckbox(project.included, () => onToggleProject(project.id))}
          <div className="flex-1 space-y-2 min-w-0">
            {editingNameId === project.id ? (
              <input
                type="text"
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onBlur={() => {
                  onUpdateProjectField(project.id, 'name', editingName)
                  setEditingNameId(null)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    onUpdateProjectField(project.id, 'name', editingName)
                    setEditingNameId(null)
                  } else if (e.key === 'Escape') {
                    setEditingNameId(null)
                  }
                }}
                placeholder="Project name"
                className="w-full px-2 py-1 text-sm font-medium border border-interactive rounded focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring"
                autoFocus
              />
            ) : (
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setEditingNameId(project.id)
                  setEditingName(project.name || '')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditingNameId(project.id)
                    setEditingName(project.name || '')
                  }
                }}
                className="text-sm font-medium text-text-primary cursor-text rounded p-1 -m-1 hover:bg-surface-muted"
              >
                {project.name || <span className="text-text-muted italic">Project name</span>}
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
              {editingCompanyId === project.id ? (
                <input
                  type="text"
                  value={editingCompany}
                  onChange={(e) => setEditingCompany(e.target.value)}
                  onBlur={() => {
                    onUpdateProjectField(project.id, 'company', editingCompany)
                    setEditingCompanyId(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onUpdateProjectField(project.id, 'company', editingCompany)
                      setEditingCompanyId(null)
                    } else if (e.key === 'Escape') {
                      setEditingCompanyId(null)
                    }
                  }}
                  placeholder="Company"
                  className="px-2 py-0.5 text-xs border border-interactive rounded"
                  autoFocus
                />
              ) : (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditingCompanyId(project.id)
                    setEditingCompany(project.company || '')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditingCompanyId(project.id)
                      setEditingCompany(project.company || '')
                    }
                  }}
                  className="cursor-text hover:text-text-secondary"
                >
                  {project.company || 'Company'}
                </span>
              )}

              <span className="text-text-muted">·</span>

              {editingStartDateId === project.id ? (
                <input
                  type="text"
                  value={editingStartDate}
                  onChange={(e) => setEditingStartDate(e.target.value)}
                  onBlur={() => {
                    onUpdateProjectField(project.id, 'startDate', editingStartDate)
                    setEditingStartDateId(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onUpdateProjectField(project.id, 'startDate', editingStartDate)
                      setEditingStartDateId(null)
                    } else if (e.key === 'Escape') {
                      setEditingStartDateId(null)
                    }
                  }}
                  placeholder="MM/YYYY"
                  className="w-20 px-1 py-0.5 text-xs border border-interactive rounded"
                  autoFocus
                />
              ) : (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditingStartDateId(project.id)
                    setEditingStartDate(project.startDate || '')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditingStartDateId(project.id)
                      setEditingStartDate(project.startDate || '')
                    }
                  }}
                  className="cursor-text hover:text-text-secondary"
                >
                  {project.startDate || 'Start'}
                </span>
              )}

              <span>–</span>

              {editingEndDateId === project.id ? (
                <input
                  type="text"
                  value={editingEndDate}
                  onChange={(e) => setEditingEndDate(e.target.value)}
                  onBlur={() => {
                    onUpdateProjectField(project.id, 'endDate', editingEndDate)
                    setEditingEndDateId(null)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      onUpdateProjectField(project.id, 'endDate', editingEndDate)
                      setEditingEndDateId(null)
                    } else if (e.key === 'Escape') {
                      setEditingEndDateId(null)
                    }
                  }}
                  placeholder="MM/YYYY"
                  className="w-20 px-1 py-0.5 text-xs border border-interactive rounded"
                  autoFocus
                />
              ) : (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setEditingEndDateId(project.id)
                    setEditingEndDate(project.endDate || '')
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setEditingEndDateId(project.id)
                      setEditingEndDate(project.endDate || '')
                    }
                  }}
                  className="cursor-text hover:text-text-secondary"
                >
                  {project.endDate || 'End'}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={WX_BULLETS_BLOCK_CLASS}>
          {bullets.map((bullet: ProjectBullet, bulletIndex) => {
            const isDragging = draggedBulletId === bullet.id
            const isDragOver = dragOverBulletIndex === bulletIndex && draggedBulletId !== bullet.id

            return (
              <div
                key={bullet.id}
                data-project-bullet-id={bullet.id}
                draggable
                onMouseEnter={() => setHoveredBulletId(bullet.id)}
                onMouseLeave={() => setHoveredBulletId(null)}
                onDragStart={(e) => {
                  e.stopPropagation()
                  setDraggedBulletId(bullet.id)
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', '')
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  e.dataTransfer.dropEffect = 'move'
                  if (draggedBulletId !== bullet.id) {
                    setDragOverBulletIndex(bulletIndex)
                  }
                }}
                onDragLeave={() => {
                  if (dragOverBulletIndex === bulletIndex) {
                    setDragOverBulletIndex(null)
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (draggedBulletId && draggedBulletId !== bullet.id) {
                    onReorderBullets(project.id, draggedBulletId, bulletIndex)
                  }
                  setDraggedBulletId(null)
                  setDragOverBulletIndex(null)
                }}
                onDragEnd={() => {
                  setDraggedBulletId(null)
                  setDragOverBulletIndex(null)
                }}
                className={`group/proj-bullet flex items-start gap-3 cursor-move ${
                  isDragging ? 'opacity-50' : ''
                } ${isDragOver ? 'border-t-2 border-interactive pt-2 -mt-2' : ''}`}
              >
                <WorkExperienceDragHandle
                  group="proj-bullet"
                  title="Перетащите пункт для изменения порядка"
                />
                <div
                  className={`flex items-start gap-3 flex-1 rounded transition-colors ${
                    hoveredBulletId === bullet.id ? 'bg-surface-muted' : ''
                  }`}
                >
                  <div className="pt-1">{renderCheckbox(bullet.included, () => onToggleBullet(project.id, bullet.id))}</div>
                  <div className="flex-1 min-w-0 relative">
                    {editingBulletId === bullet.id ? (
                      <>
                        <textarea
                          ref={bulletTextareaRef}
                          value={editingBulletText}
                          onChange={(e) => {
                            onBulletTextChange(e)
                            adjustTextareaHeight(e.target)
                          }}
                          onBlur={() => onSaveBullet(project.id, bullet.id)}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' && e.metaKey) || (e.key === 'Enter' && e.ctrlKey)) {
                              e.preventDefault()
                              onSaveBullet(project.id, bullet.id)
                            } else if (e.key === 'Escape') {
                              e.preventDefault()
                              setEditingBulletId(null)
                              setEditingBulletText('')
                            }
                          }}
                          placeholder="Achievement or detail..."
                          rows={1}
                          className="w-full pl-[5px] pt-0 pb-6 pr-8 text-sm text-text-secondary focus:outline-none resize-none overflow-hidden whitespace-pre-wrap leading-relaxed border border-transparent focus:border-border-default rounded-sm"
                          style={{ minHeight: '3rem' }}
                          autoFocus
                        />
                        <div className="absolute bottom-1 right-2 text-xs text-text-muted pointer-events-none">
                          {getSaveHint()}
                        </div>
                      </>
                    ) : (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setEditingBulletId(bullet.id)
                          setEditingBulletText(bullet.text)
                          setTimeout(() => {
                            if (bulletTextareaRef.current) {
                              adjustTextareaHeight(bulletTextareaRef.current)
                            }
                          }, 0)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setEditingBulletId(bullet.id)
                            setEditingBulletText(bullet.text)
                          }
                        }}
                        className={`w-full pt-0 pb-1 pr-6 cursor-text hover:bg-surface-muted transition-colors whitespace-pre-wrap text-sm min-h-[28px] rounded leading-relaxed ${
                          bullet.included ? 'text-text-secondary' : 'text-text-muted'
                        }`}
                      >
                        {bullet.text || (
                          <span className="text-text-muted italic">Click to edit bullet...</span>
                        )}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onDeleteBullet(project.id, bullet.id)}
                      className="absolute top-1 right-0 p-1 text-text-muted hover:text-danger opacity-0 group-hover/proj-bullet:opacity-100 transition-opacity"
                      title="Delete bullet"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )
          })}

          <button
            type="button"
            onClick={() => onAddBullet(project.id)}
            className={WX_ADD_BULLET_BUTTON_CLASS}
          >
            + Add bullet
          </button>
        </div>
      </div>
    </div>
  )
}
